/**
 * DisputeSendEmailModal
 *
 * Thin wrapper around SingleEmailModal that provides dispute-specific
 * props (source type, linked transaction IDs for invoice attachment fetching).
 *
 * Fetches linked transaction IDs internally via the dispute detail endpoint
 * rather than relying on page-level data (which may not include transactions).
 *
 * When the company is linked to Dimensions the modal fetches documents from
 * the Document API (ADM) instead of the Financial API.
 */

import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { type EmailAttachment, SingleEmailModal } from '@/components/shared/single-email-modal';
import { useAdmDocumentDownloadAsBase64 } from '@/hooks/useAdmDocumentDownloadAsBase64';
import { useApi } from '@/hooks/useApi';
import { useCompanyProductType } from '@/hooks/useCompanyProductType';
import { useDisputeByNumericId } from '@/hooks/useDisputesData';
import { useLogDisputeEmail } from '@/hooks/useLogDisputeEmail';
import { useTransactionDocumentDownload } from '@/hooks/useTransactionDocumentDownload';
import { fetchAdmDocumentsForTransaction } from '@/hooks/useTransactionDocuments';
import {
  attachmentErrorMessages,
  attachmentsError,
  emptyAttachmentsResult,
  type FetchAttachmentsResult,
  fetchAttachmentsWithProgress,
} from '@/lib/fetch-attachments-utils';
import { trackError } from '@/lib/telemetry';
import { useAuth } from '@/providers/AuthProvider';
import { useDocumentApi } from '@/providers/DocumentApiProvider';
import { useMode } from '@/providers/ModeProvider';
import type { TransactionApi } from '@/types/api/transactions';

export interface DisputeSendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disputeId: number;
  customerId: number;
  customerName?: string;
  accountNumber?: string;
  onSuccess?: () => void;
}

export function DisputeSendEmailModal({
  open,
  onOpenChange,
  disputeId,
  customerId,
  customerName = '',
  accountNumber = '',
  onSuccess,
}: Readonly<DisputeSendEmailModalProps>) {
  const { downloadDocumentAsBase64 } = useTransactionDocumentDownload();
  const { downloadAdmDocumentAsBase64 } = useAdmDocumentDownloadAsBase64();
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();
  const { isDemo } = useMode();
  const { apiGet } = useApi();
  const { isDimensions } = useCompanyProductType();
  const logDisputeEmail = useLogDisputeEmail();

  const customerIdStr = String(customerId);

  const { data: disputeDetail } = useDisputeByNumericId(open ? disputeId : undefined);

  const linkedTransactionIds = useMemo(
    () => (disputeDetail?.transactions ?? []).map((t) => t.transactionId).filter((id) => id > 0),
    [disputeDetail],
  );

  const handleFetchAttachments = useCallback(
    async (onProgress: (done: number, total: number) => void): Promise<FetchAttachmentsResult> => {
      if (linkedTransactionIds.length === 0) return emptyAttachmentsResult();
      if (isDemo) return emptyAttachmentsResult();

      if (isDimensions) {
        if (!getAccessToken) {
          return attachmentsError(attachmentErrorMessages.noAuthentication);
        }

        const ctx = await getDocumentContext();
        if (!ctx) {
          return attachmentsError(attachmentErrorMessages.noDocumentService);
        }

        const allDocs: { id: string; fileName: string; fileType: string }[] = [];
        let failedTxCount = 0;
        for (const txId of linkedTransactionIds) {
          try {
            const txDetail = await apiGet<TransactionApi>(`/api/v1/transactions/${txId}`);
            const externalId = txDetail?.externalId;
            if (!externalId) continue;

            const docs = await fetchAdmDocumentsForTransaction(ctx, getAccessToken, externalId, 10);
            for (const doc of docs) {
              allDocs.push({ id: doc.id, fileName: doc.fileName, fileType: doc.fileType });
            }
          } catch {
            failedTxCount++;
          }
        }

        if (allDocs.length === 0) {
          return failedTxCount > 0
            ? attachmentsError(attachmentErrorMessages.documentLookupFailed(failedTxCount))
            : emptyAttachmentsResult();
        }

        if (failedTxCount > 0) {
          toast.warning(`${failedTxCount} transaction(s) could not be retrieved for document lookup`);
        }

        return fetchAttachmentsWithProgress(
          allDocs,
          (doc) => downloadAdmDocumentAsBase64(doc.id, doc.fileName, doc.fileType),
          onProgress,
        );
      }

      return fetchAttachmentsWithProgress(linkedTransactionIds, downloadDocumentAsBase64, onProgress);
    },
    [
      linkedTransactionIds,
      isDemo,
      isDimensions,
      getDocumentContext,
      apiGet,
      getAccessToken,
      downloadAdmDocumentAsBase64,
      downloadDocumentAsBase64,
    ],
  );

  const handleAfterSend = useCallback(
    (payload: {
      toEmails: string[];
      ccEmails: string[];
      subject: string;
      htmlBody: string;
      templateId?: string;
      templateName?: string;
      isSubjectModified: boolean;
      isBodyModified: boolean;
      attachOutstandingInvoices: boolean;
      attachedTransactionIds?: number[];
    }) => {
      logDisputeEmail.mutate(
        {
          sourceId: String(disputeId),
          toRecipients: payload.toEmails,
          ccRecipients: payload.ccEmails.length > 0 ? payload.ccEmails : undefined,
          subject: payload.subject,
          htmlBody: payload.htmlBody,
          templateId: payload.templateId ? parseInt(payload.templateId, 10) : undefined,
          templateName: payload.templateName,
          isSubjectModified: payload.isSubjectModified,
          isBodyModified: payload.isBodyModified,
          attachOutstandingInvoices: payload.attachOutstandingInvoices,
          attachedTransactionIds: payload.attachedTransactionIds,
        },
        {
          onError: (error) => {
            trackError(error instanceof Error ? error : new Error('Failed to save dispute email to history'));
            toast.error('Email was sent but failed to save to history. Please contact support if the issue persists.');
          },
        },
      );
    },
    [logDisputeEmail, disputeId],
  );

  return (
    <SingleEmailModal
      open={open}
      onOpenChange={onOpenChange}
      title="Send Email"
      customerId={customerIdStr}
      customerName={customerName}
      accountNumber={accountNumber}
      sourceType="dispute"
      sourceId={disputeId}
      transactionIds={linkedTransactionIds}
      callbackEventName="creditcontrol:dispute-email-done"
      attachLabel="Attach outstanding invoices"
      fetchAttachments={handleFetchAttachments}
      onAfterSend={handleAfterSend}
      onSuccess={onSuccess}
      allowedCategoryTypes={['disputes', 'general']}
      allowedTemplateTypes={['email']}
    />
  );
}
